import { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input.jsx";

// List of popular locations for tech job searches in the US
export const US_TECH_LOCATIONS = [
    // Major tech cities
    "San Francisco, CA", "New York, NY", "Seattle, WA", "Boston, MA", "Austin, TX",
    "Los Angeles, CA", "Chicago, IL", "Denver, CO", "San Jose, CA", "Atlanta, GA",
    "Washington, DC", "Dallas, TX", "Raleigh, NC", "Portland, OR", "San Diego, CA",
    "Phoenix, AZ", "Philadelphia, PA", "Pittsburgh, PA", "Minneapolis, MN", "Miami, FL",
    "Salt Lake City, UT", "Houston, TX", "Nashville, TN", "Charlotte, NC", "Tampa, FL",
    "Boulder, CO", "Columbus, OH", "Detroit, MI", "Cincinnati, OH", "Madison, WI",
    "St. Louis, MO", "Baltimore, MD", "Orlando, FL", "Kansas City, MO", "Indianapolis, IN",
    "Sacramento, CA", "Las Vegas, NV", "Huntsville, AL", "Richmond, VA", "Jacksonville, FL",
    "Ann Arbor, MI", "Durham, NC", "San Antonio, TX", "Rochester, NY", "Cleveland, OH",
    "Buffalo, NY", "Memphis, TN", "Milwaukee, WI", "Louisville, KY", "Albuquerque, NM",

    // Metropolitan areas and geographic regions
    "Silicon Valley", "Bay Area", "Greater Boston Area", "NYC Metro Area", "Seattle Metro Area",
    "Research Triangle", "DMV Area", "Greater Los Angeles", "Greater Chicago Area",
    "Greater Atlanta Area", "Dallas-Fort Worth Metroplex", "Denver-Boulder Corridor",
    "Greater Washington DC Area", "Greater Philadelphia Area", "South Florida",
    "Twin Cities Metro", "Greater Phoenix Area", "Greater Austin Area", "Pacific Northwest",
    "Greater San Diego Area", "Greater Houston Area", "Pittsburgh Metro Area",
    "Greater Nashville Area", "Greater Salt Lake City Area", "Raleigh-Durham Area",
    "Greater Portland Area", "Northern Virginia", "Greater Miami Area", "Greater Detroit Area",

    // States
    "California", "New York", "Washington", "Massachusetts", "Texas", "Colorado", "Illinois",
    "Georgia", "North Carolina", "Oregon", "Maryland", "Virginia", "Florida", "Pennsylvania",
    "New Jersey", "Arizona", "Minnesota", "Utah", "Michigan", "Ohio", "Tennessee", "Wisconsin",
    "Missouri", "Indiana", "Nevada", "Connecticut", "Alabama", "South Carolina", "Kentucky",
    "Oklahoma", "Louisiana", "Iowa", "Kansas", "New Mexico", "Idaho", "Rhode Island", "Nebraska",
    "West Virginia", "New Hampshire", "Maine", "Mississippi", "Arkansas", "Hawaii", "Delaware",
    "Alaska", "Montana", "South Dakota", "North Dakota", "Vermont", "Wyoming",

    // Important areas within cities and specific tech environments
    "Palo Alto, CA", "Mountain View, CA", "Sunnyvale, CA", "Cupertino, CA", "Menlo Park, CA",
    "Redmond, WA", "Bellevue, WA", "Cambridge, MA", "Kirkland, WA", "Santa Monica, CA",
    "Plano, TX", "Irvine, CA", "Redwood City, CA", "Santa Clara, CA", "Fremont, CA",
    "Arlington, VA", "Tempe, AZ", "Reston, VA", "Tysons Corner, VA", "Bethesda, MD",
    "Oakland, CA", "Berkeley, CA", "Jersey City, NJ", "Scottsdale, AZ", "Brooklyn, NY",
    "Fort Collins, CO", "Beaverton, OR", "Alpharetta, GA", "Cary, NC", "Irving, TX",

    // Known innovation and technology districts
    "Route 128 Corridor, MA", "Research Park, NC", "Tech Square, GA", "South of Market, SF",
    "Midtown Atlanta Tech Hub", "Cambridge Innovation Center", "Austin Technology Incubator",
    "Seattle's South Lake Union", "Boston Seaport Innovation District", "NYC Tech Hub",
    "Chicago's Merchandise Mart", "University City, Philadelphia", "Kendall Square, Cambridge",
    "Innovation District, Boston", "RTP, North Carolina", "Mission Bay, San Francisco",

    // Secondary but important tech areas
    "Boise, ID", "Des Moines, IA", "Omaha, NE", "Tulsa, OK", "Provo, UT",
    "Fort Lauderdale, FL", "Tucson, AZ", "Birmingham, AL", "Columbia, SC", "Charleston, SC",
    "Wilmington, DE", "Greenville, SC", "Oklahoma City, OK", "Manchester, NH", "Providence, RI",
    "Eugene, OR", "Honolulu, HI", "Springfield, MA", "Chattanooga, TN", "Asheville, NC",

    // Major cities not mentioned earlier
    "New Orleans, LA", "Anaheim, CA", "Santa Fe, NM", "Aurora, CO", "Fort Worth, TX",
    "Greensboro, NC", "Long Beach, CA", "Savannah, GA", "Syracuse, NY", "Tacoma, WA",
    "Worcester, MA", "Tallahassee, FL", "Spokane, WA", "Chandler, AZ", "Mesa, AZ"
];

export default function LocationAutocomplete({
    onSelect,
    placeholder = "Enter location",
    className = "",
    value = "",
    onChange,
    error = false
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [lastSelectedValue, setLastSelectedValue] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Handle clicks outside the component
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Update suggestions when value changes
    useEffect(() => {
        // Don't show suggestions if the value hasn't changed since last selection
        if (value === lastSelectedValue && value !== "") {
            return;
        }

        if (value && value.length > 1) {
            const matches = US_TECH_LOCATIONS.filter((loc) =>
                loc.toLowerCase().includes(value.toLowerCase())
            ).slice(0, 7);
            
            setSuggestions(matches);
            // Only show suggestions if we have matches AND the input is focused
            if (matches.length > 0 && document.activeElement === inputRef.current) {
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        setHighlightIndex(-1);
    }, [value, lastSelectedValue]);

    const handleSelect = (val) => {
        if (onSelect) onSelect(val);
        if (onChange) onChange({ target: { value: val } });
        setShowSuggestions(false);
        setLastSelectedValue(val);
        
        // Explicitly blur the input to move focus away
        if (inputRef.current) {
            inputRef.current.blur();
        }
    };

    const handleInputChange = (e) => {
        if (onChange) onChange(e);
        // If user is typing, they're not using the last selection anymore
        if (e.target.value !== lastSelectedValue) {
            setLastSelectedValue("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === "Enter" && highlightIndex >= 0 && showSuggestions) {
            e.preventDefault();
            handleSelect(suggestions[highlightIndex]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    return (
        <div className={`relative ${className}`} ref={wrapperRef}>
            <Input
                ref={inputRef}
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    // Only show suggestions if we have matches AND the value isn't the last selected one
                    if (value && value.length > 1 && suggestions.length > 0 && value !== lastSelectedValue) {
                        setShowSuggestions(true);
                    }
                }}
                className={`${error 
                    ? 'bg-red-50 border-red-200 hover:border-red-400 focus:border-red-500 focus:ring-red-100' 
                    : 'bg-[#e9f2ff] border-blue-200 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100'
                } text-gray-800 placeholder-gray-500 rounded-lg h-12 text-base shadow-sm transition-all`}
            />

            {showSuggestions && suggestions.length > 0 && (
                <ul
                    className="absolute z-10 w-full bg-white border mt-1 rounded shadow-lg max-h-60 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur while clicking on suggestions
                >
                    {suggestions.map((loc, index) => (
                        <li
                            key={loc}
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent default to keep focus
                                handleSelect(loc);
                            }}
                            className={`p-2 cursor-pointer ${
                                index === highlightIndex ? 'bg-blue-100' : 'hover:bg-gray-100'
                            }`}
                        >
                            {loc}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}